// dependencies
import { List, Map, fromJS } from "immutable";

// base
export const settingsHaveBeenFetched = (state) => state.settings.get("company").size > 0;

export const getCompanySettingsContext = (state) => state.settings.get("company", Map());

// goals
export const getGoalDefaultTransparency = (state) => {
	const isPrivate = state.settings.getIn(["company", "goals", "attributes", "defaultGoalsToPrivate"], false);
	return isPrivate ? "private" : "public";
};

export const getGoalWeights = (state) => state.settings.getIn(["company", "goals", "attributes", "goalWeights"], false);

export const getRequireGoalCategory = (state) =>
	state.settings.getIn(["company", "goals", "attributes", "requireGoalCategory"], false);

export const getIsAlignmentVisibleToAllUsers = (state) =>
	state.settings.getIn(["company", "goals", "attributes", "treeVisibleToAllUsers"], false);

export const getIsGoalDashboardEnabled = (state) =>
	state.settings.getIn(["company", "goalDashboard", "state"], "disabled") === "enabled";

export const getIsCompanyDocumentSigningEnabled = (state) =>
	state.settings.getIn(["company", "documentSigning", "state"], "disabled") === "enabled";

export const getCompanyDecimalPlacesSetting = (state) => state.settings.get("decimalPlaces", 3);

export const getCompanyLimit = (state, name, notSetValue = null) => {
	const limitNames = {
		MAXFORMSPERREVIEWTEMPLATE: "maxFormsPerTemplate",
	};

	const limitKey = limitNames[name];

	if (limitKey) {
		return state.settings.getIn(["limits", limitKey], notSetValue);
	}

	return notSetValue;
};

export const getSamlState = (state) => state.settings.getIn(["company", "saml", "state"], null);
export const getEnforceLoginMethodsState = (state) =>
	state.settings.getIn(["company", "enforceLoginMethods", "state"], null);
export const getAuthenticationMethodsState = (state) =>
	state.settings.getIn(["company", "enforceLoginMethods", "state"], null);

export const getSCIMState = (state) => state.settings.getIn(["company", "scim_api", "state"], null);

export const getBetaFeatures = (state) => {
	const result = state.settings.getIn(["betaFeatures"], List());
	if (result === null) {
		return [];
	}
	return result.toJS();
};

export const getCompanyLicenses = (state) => {
	const result = state.settings.getIn(["licenses"], List());
	if (result === null) {
		return [];
	}
	return result.toJS();
};

export const getCustomBilling = (state) => state.settings.getIn(["customBilling"], false);

/**
 * Get the asap and deferred notifications list
 */
export const getAsapAndDeferredNotifications = (state) => {
	const settings = state.settings.getIn(["asapAndDeferredNotifications"], List());

	return settings.map((setting) => {
		const id = setting.get("name", "");

		return Map({
			name: id,
			state: setting.get("type", ""),
			label: setting.get("label", ""),
			category: setting.get("category", ""),
		});
	});
};

/**
 * Gets the asap and deferred notifications grouped by category.
 */
export const getAsapAndDeferredNotificationsGroupings = (state) => {
	const notifications = getAsapAndDeferredNotifications(state);

	const groups = {};
	const categories = [];

	// group everything together
	notifications.forEach((notification) => {
		const category = notification.get("category");

		if (categories.indexOf(category) === -1) {
			groups[category] = [];
			categories.push(category);
		}

		groups[category].push(notification);
	});

	// sort categories according to the specified order
	const groupOrder = ["Review", "Goal", "Compensation", "Feedback", "To-Do", "Manager"];
	categories.sort((a, b) => groupOrder.indexOf(a) - groupOrder.indexOf(b));

	// set the ordering of the notifications within the groups
	const categoryOrdering = {
		Review: [
			"formAboutYouCompleted",
			"formDueWithin24Hours",
			"formDueWithin72Hours",
			"formOverdueDaily",
			"formSentBack",
			"formsToAuthor",
			"formsToSign",
			"reviewReportNeedsApproval",
		],
		Goal: [
			"goalAssigned",
			"goalUpdate",
			"goalDueWithin24Hours",
			"goalShared",
			"sharedGoalUpdated",
			"teamMemberGoalAssigned",
			"teamMemberGoalUpdate",
			"teamMemberGoalDueWithin24Hours",
		],
		Compensation: [
			"compensationDecisionAvailableToSubmit",
			"compensationSubmissionDueWithin24Hours",
			"compensationSubmissionDueWithin72Hours",
			"overdueCompensationDecisions",
			"removedFromTeamBudget",
		],
		Feedback: ["receivedFeedback", "directReportReceivedFeedback", "allPublicRerecognition"],
		"To-Do": ["toDoDueWithin24Hours", "toDoStart"],
		Manager: ["newDirectReport"],
	};

	// sort in specified order
	categories.forEach((category) => {
		const ordering = categoryOrdering[category] ? categoryOrdering[category] : [];
		groups[category].sort((a, b) => ordering.indexOf(a.get("name")) - ordering.indexOf(b.get("name")));
	});

	return {
		notifications: fromJS(groups),
		categories: List(categories),
	};
};

/**
 * Get the deferred delivery settings from the store
 */
export const selectDeferredFrequencySettings = (state) => {
	const setting = state.settings.getIn(["deferredFrequencySettings"], List());
	return setting.filter((item) => item.get("hour") !== null);
};

/**
 * Get the digest notifications from the store
 */
export const selectWeeklyDigestNotificationsGroupings = (state) => {
	const notifications = state.settings.getIn(["digestNotifications"], List()).map((item) =>
		Map({
			name: item.get("name", ""),
			state: item.get("type", ""),
			label: item.get("label", ""),
			category: item.get("category", ""),
		})
	);

	const groups = {};
	const categories = [];

	// group everything together
	notifications.forEach((notification) => {
		const category = notification.get("category");

		if (categories.indexOf(category) === -1) {
			groups[category] = [];
			categories.push(category);
		}

		groups[category].push(notification);
	});

	// sort categories according to the specified order
	const groupOrder = ["Review", "Goal", "Compensation", "To-Do", "Manager"];
	categories.sort((a, b) => groupOrder.indexOf(a) - groupOrder.indexOf(b));

	// set the ordering of the notifications within the groups
	const categoryOrdering = {
		Review: [
			"formsDueWithinWeek",
			"formsOverdueAuthor",
			"formsOverdueSigner",
			"formsOverdueWhereAwaitingSubmission",
			"numOfOpenForms",
			"reviewReportNeedsApprovalDigest",
			"directReportActiveReviews",
		],
		Goal: [
			"goalDueInTheNextMonth",
			"goalDueInTheNextWeek",
			"goalsOverdue",
			"numOfUnreadGoalUpdates",
			"teamMemberGoalsDueInTheNextMonth",
			"teamMemberGoalsDueThisWeek",
			"teamMemberGoalsOverdue",
			"numOfUnreadTeamMemberGoalUpdates",
		],
		Compensation: ["weeklyDigestCompensation"],
		"To-Do": ["toDosDueInTheNextWeek", "toDosOpenToWorkOn", "toDosOverdue"],
		Manager: ["directReportWorkAnniversaries"],
	};

	// sort by specified order
	categories.forEach((category) => {
		const ordering = categoryOrdering[category] ? categoryOrdering[category] : [];
		groups[category].sort((a, b) => ordering.indexOf(a.get("name")) - ordering.indexOf(b.get("name")));
	});

	return {
		notifications: fromJS(groups),
		categories: List(categories),
	};
};

/**
 * Get the admin digest notifications from the store
 */
export const selectAdminWeeklyDigestNotificationsGroupings = (state) => {
	const notifications = state.settings.getIn(["adminDigestNotifications"], List()).map((item) =>
		Map({
			name: item.get("name", ""),
			state: item.get("type", ""),
			label: item.get("label", ""),
			category: item.get("category", ""),
		})
	);

	const groups = {};
	const categories = [];

	// group everything together
	notifications.forEach((notification) => {
		const category = notification.get("category");

		if (categories.indexOf(category) === -1) {
			groups[category] = [];
			categories.push(category);
		}

		groups[category].push(notification);
	});

	// sort categories according to the specified order
	const groupOrder = ["Admin"];
	categories.sort((a, b) => groupOrder.indexOf(a) - groupOrder.indexOf(b));

	// set the ordering of the notifications within the groups
	const categoryOrdering = {
		Admin: [
			"addedUsers",
			"deactivatedUsers",
			"dataFieldChanges",
			"workAnniversaries",
			"reviewReportNeedsApprovalTotal",
		],
	};

	// sort alphabetically
	categories.forEach((category) => {
		const ordering = categoryOrdering[category] ? categoryOrdering[category] : [];
		groups[category].sort((a, b) => ordering.indexOf(a.get("name")) - ordering.indexOf(b.get("name")));
	});

	return {
		notifications: fromJS(groups),
		categories: List(categories),
	};
};

export const selectPossibleTimezones = (state) => state.settings.getIn(["possibleTimezones"], List());
export const selectDigestDeliverySettings = (state, notSetValue = Map()) =>
	state.settings.getIn(["digestDeliverySettings"], notSetValue);
export const selectAdminDigestDeliverySettings = (state, notSetValue = Map()) =>
	state.settings.getIn(["adminDigestDeliverySettings"], notSetValue);

/**
 * Select the asap and daily notification settings for the company's template
 */
export const selectAsapAndDeferredCompanyNotificationsGroupings = (state) => {
	const notifications = state.settings
		.getIn(["notificationsCompanyTemplates", "asapAndDeferredNotifications"], List())
		.map((item) =>
			Map({
				name: item.get("name", ""),
				state: item.get("type", ""),
				label: item.get("label", ""),
				category: item.get("category", ""),
			})
		);

	const groups = {};
	const categories = [];

	// group everything together
	notifications.forEach((notification) => {
		const category = notification.get("category");

		if (categories.indexOf(category) === -1) {
			groups[category] = [];
			categories.push(category);
		}

		groups[category].push(notification);
	});

	// sort categories according to the specified order
	const groupOrder = ["Review", "Goal", "Compensation", "Feedback", "To-Do", "Manager"];
	categories.sort((a, b) => groupOrder.indexOf(a) - groupOrder.indexOf(b));

	// set the ordering of the notifications within the groups
	const categoryOrdering = {
		Review: [
			"formAboutYouCompleted",
			"formDueWithin24Hours",
			"formDueWithin72Hours",
			"formOverdueDaily",
			"formSentBack",
			"formsToAuthor",
			"formsToSign",
			"reviewReportNeedsApproval",
		],
		Goal: [
			"goalAssigned",
			"goalUpdate",
			"goalDueWithin24Hours",
			"goalShared",
			"sharedGoalUpdated",
			"teamMemberGoalUpdate",
			"teamMemberGoalDueWithin24Hours",
		],
		Compensation: [
			"compensationDecisionAvailableToSubmit",
			"compensationSubmissionDueWithin24Hours",
			"compensationSubmissionDueWithin72Hours",
			"overdueCompensationDecisions",
			"removedFromTeamBudget",
		],
		Feedback: ["receivedFeedback", "directReportReceivedFeedback", "allPublicRerecognition"],
		"To-Do": ["toDoDueWithin24Hours", "toDoStart"],
		Manager: ["newDirectReport"],
	};

	// sort in specified order
	categories.forEach((category) => {
		const ordering = categoryOrdering[category] ? categoryOrdering[category] : [];
		groups[category].sort((a, b) => ordering.indexOf(a.get("name")) - ordering.indexOf(b.get("name")));
	});

	return {
		notifications: fromJS(groups),
		categories: List(categories),
	};
};

/**
 * Select the digest notification settings for the company's template
 */
export const selectWeeklyDigestCompanyNotificationsGroupings = (state) => {
	const notifications = state.settings
		.getIn(["notificationsCompanyTemplates", "digestNotifications"], List())
		.map((item) =>
			Map({
				name: item.get("name", ""),
				state: item.get("type", ""),
				label: item.get("label", ""),
				category: item.get("category", ""),
			})
		);

	const groups = {};
	const categories = [];

	// group everything together
	notifications.forEach((notification) => {
		const category = notification.get("category");

		if (categories.indexOf(category) === -1) {
			groups[category] = [];
			categories.push(category);
		}

		groups[category].push(notification);
	});

	// sort categories according to the specified order
	const groupOrder = ["Review", "Goal", "Compensation", "To-Do", "Manager"];
	categories.sort((a, b) => groupOrder.indexOf(a) - groupOrder.indexOf(b));

	// set the ordering of the notifications within the groups
	const categoryOrdering = {
		Review: [
			"formsDueWithinWeek",
			"formsOverdueAuthor",
			"formsOverdueSigner",
			"formsOverdueWhereAwaitingSubmission",
			"numOfOpenForms",
			"reviewReportNeedsApprovalDigest",
			"directReportActiveReviews",
		],
		Goal: [
			"goalDueInTheNextMonth",
			"goalDueInTheNextWeek",
			"goalsOverdue",
			"numOfUnreadGoalUpdates",
			"teamMemberGoalsDueInTheNextMonth",
			"teamMemberGoalsDueThisWeek",
			"teamMemberGoalsOverdue",
			"numOfUnreadTeamMemberGoalUpdates",
		],
		Compensation: ["weeklyDigestCompensation"],
		"To-Do": ["toDosDueInTheNextWeek", "toDosOpenToWorkOn", "toDosOverdue"],
		Manager: ["directReportWorkAnniversaries"],
	};

	// sort alphabetically
	categories.forEach((category) => {
		const ordering = categoryOrdering[category] ? categoryOrdering[category] : [];
		groups[category].sort((a, b) => ordering.indexOf(a.get("name")) - ordering.indexOf(b.get("name")));
	});

	return {
		notifications: fromJS(groups),
		categories: List(categories),
	};
};

/**
 * Select the admin digest notification settings for the company's template
 */
export const selectAdminWeeklyDigestCompanyNotificationsGroupings = (state) => {
	const notifications = state.settings
		.getIn(["notificationsCompanyTemplates", "adminDigestNotifications"], List())
		.map((item) =>
			Map({
				name: item.get("name", ""),
				state: item.get("type", ""),
				label: item.get("label", ""),
				category: item.get("category", ""),
			})
		);

	const groups = {};
	const categories = [];

	// group everything together
	notifications.forEach((notification) => {
		const category = notification.get("category");

		if (categories.indexOf(category) === -1) {
			groups[category] = [];
			categories.push(category);
		}

		groups[category].push(notification);
	});

	// sort categories according to the specified order
	const groupOrder = ["Admin"];
	categories.sort((a, b) => groupOrder.indexOf(a) - groupOrder.indexOf(b));

	// set the ordering of the notifications within the groups
	const categoryOrdering = {
		Admin: [
			"addedUsers",
			"deactivatedUsers",
			"dataFieldChanges",
			"workAnniversaries",
			"reviewReportNeedsApprovalTotal",
		],
	};

	// sort alphabetically
	categories.forEach((category) => {
		const ordering = categoryOrdering[category] ? categoryOrdering[category] : [];
		groups[category].sort((a, b) => ordering.indexOf(a.get("name")) - ordering.indexOf(b.get("name")));
	});

	return {
		notifications: fromJS(groups),
		categories: List(categories),
	};
};

/**
 * Get the deferred delivery settings from the company's template
 */
export const selectDeferredFrequencyCompanySettings = (state) => {
	const setting = state.settings.getIn(["notificationsCompanyTemplates", "deferredFrequencySettings"], List());
	return setting.filter((item) => item.get("hour") !== null);
};

export const selectCompanyMfaEnabled = (state) => state.settings.getIn(["companyMfaEnabled"], false);
export const selectCompanyDigestDeliverySettings = (state, notSetValue = Map()) =>
	state.settings.getIn(["notificationsCompanyTemplates", "digestDeliverySettings"], notSetValue);
export const selectCompanyAdminDigestDeliverySettings = (state, notSetValue = Map()) =>
	state.settings.getIn(["notificationsCompanyTemplates", "adminDigestDeliverySettings"], notSetValue);
